import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, Music, Video, FileText } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { DISTRICTS, GOTRAMS, SHAKHAS, SUTRAMS } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import FreeTextSelect from "@/components/FreeTextSelect";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Toranam } from "@/components/Ornaments";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";

const init = {
  name: "", mobile: "", email: "", gotram: "", gotram_custom: "", shakha: "", sutram: "",
  agama: "", living_preference: "both", experience_years: "", district: "", state: "Andhra Pradesh",
  languages: "", bio: "", photo: "", mantra_audio: "", pooja_videos: [], certifications: [], specializations: "",
};

export default function ArchakaRegister() {
  const { t } = useApp();
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [ytLink, setYtLink] = useState("");
  const [status, setStatus] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!user) return;
    if (user.name && !f.name) set("name", user.name);
    if (user.mobile && !f.mobile) set("mobile", user.mobile);
    if (user.email && !f.email) set("email", user.email);
    api.get("/me/archaka").then(({ data }) => { if (data && data.id) { setF((p) => ({ ...p, ...data, experience_years: data.experience_years || "" })); setStatus(data.status); } }).catch(() => {});
  }, [user]);

  const genBio = async () => {
    if (!f.gotram && !f.agama && !f.specializations) return toast.error("ముందుగా గోత్రం/ఆగమం/నైపుణ్యాలు నింపండి");
    setAiBusy(true);
    try {
      const points = `పేరు: ${f.name}, గోత్రం: ${f.gotram}, శాఖ: ${f.shakha}, ఆగమం: ${f.agama}, అనుభవం: ${f.experience_years} సంవత్సరాలు, నైపుణ్యాలు: ${f.specializations}, జిల్లా: ${f.district}`;
      const { data } = await api.post("/ai/generate-bio", { points });
      set("bio", data.bio); toast.success("AI బయో రూపొందించబడింది ✨");
    } catch (e) { toast.error("AI బయో విఫలమైంది"); } finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (!user) return nav("/auth?mode=signup&next=/archaka-register");
    if (user.role !== "archaka" && user.role !== "admin") return toast.error("ఈ పేజీ అర్చకుల కోసం మాత్రమే");
    if (!f.name || !f.mobile || !f.gotram || !f.district) return toast.error("తప్పనిసరి వివరాలు నింపండి (పేరు, మొబైల్, గోత్రం, జిల్లా)");
    setBusy(true);
    try {
      await api.post("/archakas", { ...f, experience_years: parseInt(f.experience_years) || 0 });
      toast.success("ప్రొఫైల్ సమర్పించబడింది! అడ్మిన్ ఆమోదం కోసం వేచి ఉండండి 🙏");
      nav("/dashboard");
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "లోపం"); } finally { setBusy(false); }
  };

  const addYt = () => { if (ytLink.trim()) { set("pooja_videos", [...f.pooja_videos, ytLink.trim()]); setYtLink(""); } };

  return (
    <div className="parchment-texture min-h-screen">
      <div className="bg-gradient-to-r from-maroon to-saffron py-10 text-center">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">🧎 అర్చక నమోదు</h1>
        <p className="text-white/85 font-telugu mt-2">మీ వైదిక వంశ పరంపర & ప్రొఫైల్ వివరాలు</p>
      </div>
      <Toranam />

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-5">
        {status && (
          <div className="p-3 rounded-xl bg-marigold/15 border border-goldline/40 text-center font-telugu text-maroon text-sm" data-testid="archaka-status-banner">
            మీ ప్రొఫైల్ స్థితి: <Badge className={status === "approved" ? "bg-green-100 text-green-800" : status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}>{t(status)}</Badge>
          </div>
        )}

        <Section title="👤 ప్రాథమిక వివరాలు">
          <FileUpload testid="ar-photo" label="ప్రొఫైల్ ఫోటో" value={f.photo} onChange={(v) => set("photo", v)} />
          <Field label="పూర్తి పేరు *"><Input data-testid="ar-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="gold-border font-telugu" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="మొబైల్ *"><Input data-testid="ar-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} className="gold-border" maxLength={10} /></Field>
            <Field label="ఇమెయిల్"><Input data-testid="ar-email" value={f.email} onChange={(e) => set("email", e.target.value)} className="gold-border" /></Field>
          </div>
          <Field label="జిల్లా *">
            <FreeTextSelect testid="ar-district" value={f.district} onChange={(v) => set("district", v)} options={DISTRICTS} placeholder="జిల్లా ఎంచుకోండి లేదా టైప్ చేయండి" />
          </Field>
        </Section>

        <div className="bg-surface rounded-2xl gold-border temple-shadow p-5 ring-2 ring-saffron/30">
          <h3 className="font-serifte font-bold text-maroon text-lg mb-1">🪷 వైదిక వంశ పరంపర / గోత్ర వివరాలు</h3>
          <p className="text-xs text-muted-foreground font-telugu mb-3">Vedic Lineage &amp; Identity</p>
          <div className="space-y-3">
            <Field label="గోత్రం (Rishi Gotram) *">
              <FreeTextSelect testid="ar-gotram" value={f.gotram} onChange={(v) => set("gotram", v)} options={GOTRAMS} placeholder="గోత్రం ఎంచుకోండి లేదా టైప్ చేయండి" />
            </Field>
            <Field label="శాఖ (Shakha)">
              <FreeTextSelect testid="ar-shakha" value={f.shakha} onChange={(v) => set("shakha", v)} options={SHAKHAS} placeholder="శాఖ ఎంచుకోండి లేదా టైప్ చేయండి" />
            </Field>
            <Field label="సూత్రం (Sutram)">
              <FreeTextSelect testid="ar-sutram" value={f.sutram} onChange={(v) => set("sutram", v)} options={SUTRAMS} placeholder="సూత్రం ఎంచుకోండి లేదా టైప్ చేయండి" />
            </Field>
            <Field label="ఆగమం (Agama)"><Input data-testid="ar-agama" value={f.agama} onChange={(e) => set("agama", e.target.value)} className="gold-border font-telugu" placeholder="వైఖానస / శైవాగమం..." /></Field>
          </div>
        </div>

        <Section title="🏡 నివాస ప్రాధాన్యత">
          <Label className="font-telugu text-maroon text-sm">దేవాలయ నియామకానికి మీ ప్రాధాన్యత?</Label>
          <RadioGroup value={f.living_preference} onValueChange={(v) => set("living_preference", v)} className="mt-2 space-y-2" data-testid="ar-living">
            {[["family", "కుటుంబంతో కలిసి వెళ్లగలను (With Family)"], ["bachelor", "ఒంటరిగా / బ్యాచిలర్‌గా మాత్రమే (Bachelor Only)"], ["both", "పరిస్థితిని బట్టి రెండింటికీ సిద్ధం (Open to Both)"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 p-2 rounded-lg border border-goldline/40 cursor-pointer hover:bg-marigold/5">
                <RadioGroupItem value={v} id={`liv-${v}`} /><span className="font-telugu text-sm text-maroon">{l}</span>
              </label>
            ))}
          </RadioGroup>
        </Section>

        <Section title="📜 అనుభవం & నైపుణ్యాలు">
          <div className="grid grid-cols-2 gap-2">
            <Field label="అనుభవం (సంవత్సరాలు)"><Input data-testid="ar-exp" type="number" value={f.experience_years} onChange={(e) => set("experience_years", e.target.value)} className="gold-border" /></Field>
            <Field label="భాషలు"><Input data-testid="ar-lang" value={f.languages} onChange={(e) => set("languages", e.target.value)} className="gold-border font-telugu" placeholder="తెలుగు, సంస్కృతం" /></Field>
          </div>
          <Field label="ప్రత్యేక నైపుణ్యాలు"><Input data-testid="ar-spec" value={f.specializations} onChange={(e) => set("specializations", e.target.value)} className="gold-border font-telugu" placeholder="అభిషేకం, హోమాలు..." /></Field>
          <div>
            <div className="flex items-center justify-between"><Label className="font-telugu text-maroon text-sm">పరిచయం (Bio)</Label>
              <Button data-testid="ar-ai-bio" type="button" size="sm" variant="outline" onClick={genBio} disabled={aiBusy} className="border-saffron text-saffron font-telugu">
                {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} AI బయో
              </Button>
            </div>
            <Textarea data-testid="ar-bio" value={f.bio} onChange={(e) => set("bio", e.target.value)} className="gold-border font-telugu mt-1" rows={3} />
          </div>
        </Section>

        <Section title="🎬 మల్టీమీడియా పోర్ట్‌ఫోలియో (ఐచ్ఛికం)">
          <FileUpload testid="ar-audio" label="🎵 మంత్ర పఠనం (30 సెకన్ల ఆడియో - ఐచ్ఛికం)" accept="audio/*" value={f.mantra_audio} onChange={(v) => set("mantra_audio", v)} />
          <div>
            <Label className="font-telugu text-maroon text-sm">🎥 పూజా వీడియోలు (YouTube/Drive లింక్)</Label>
            <div className="flex gap-2 mt-1">
              <Input data-testid="ar-yt" value={ytLink} onChange={(e) => setYtLink(e.target.value)} className="gold-border" placeholder="https://youtube.com/..." />
              <Button type="button" onClick={addYt} className="btn-sacred border-0" data-testid="ar-yt-add">జోడించు</Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">{f.pooja_videos.map((v, i) => <Badge key={i} variant="outline" className="border-goldline/50 font-telugu">{v.slice(0, 24)}... <button onClick={() => set("pooja_videos", f.pooja_videos.filter((x) => x !== v))} className="ml-1">×</button></Badge>)}</div>
            <div className="mt-2"><FileUpload testid="ar-video-file" label="లేదా వీడియో ఫైల్ అప్‌లోడ్ చేయండి" accept="video/*" multiple value={f.pooja_videos.filter((v) => v.startsWith("http") && !v.includes("youtu"))} onChange={(v) => set("pooja_videos", [...f.pooja_videos.filter((x) => x.includes("youtu")), ...v])} /></div>
          </div>
          <FileUpload testid="ar-certs" label="📄 ధృవీకరణ పత్రాలు (వేద పాఠశాల / ఆగమ ప్రవీణ)" multiple value={f.certifications} onChange={(v) => set("certifications", v)} />
        </Section>

        <Button data-testid="ar-submit" onClick={submit} disabled={busy} className="w-full btn-sacred border-0 font-telugu text-base py-6">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : `🙏 ${t("submit")}`}
        </Button>
        <p className="text-center text-xs text-muted-foreground font-telugu">సమర్పించిన తర్వాత మీ ప్రొఫైల్ అడ్మిన్ పరిశీలనలో ఉంటుంది</p>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-surface rounded-2xl gold-border temple-shadow p-5">
    <h3 className="font-serifte font-bold text-maroon text-lg mb-3">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);
const Field = ({ label, children }) => (<div><Label className="font-telugu text-maroon text-sm">{label}</Label><div className="mt-1">{children}</div></div>);
